import axios, { AxiosRequestConfig } from 'axios';
import { AdminClientCache, defaultCache, CacheTTL } from './cache';

// Coupon Types
export interface ClientCouponInterface {
  id: number;
  stripe_coupon_id: string;
  name: string;
  duration: string;
  duration_in_months?: number;
  amount_off?: number;
  percent_off?: number;
  currency?: string;
  max_redemptions?: number;
  times_redeemed: number;
  valid_until?: Date;
  is_active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface GetCouponsInputInterface {
  limit?: number;
  search?: string;
  is_active?: boolean;
  duration?: string;
  orderBy?: string;
  orderDirection?: string;
  beforeId?: number;
  afterId?: number;
  getCount?: boolean;
}

export interface GetCouponsResponseInterface {
  success: boolean;
  coupons: ClientCouponInterface[];
  total: number;
  message?: string;
}

export interface GetCouponInputInterface {
  id: number;
}

export interface GetCouponResponseInterface {
  success: boolean;
  coupon: ClientCouponInterface | null;
  message?: string;
}

export interface CreateCouponInputInterface {
  name: string;
  duration: 'once' | 'repeating' | 'forever';
  duration_in_months?: number;
  amount_off?: number;
  percent_off?: number;
  currency?: string;
  max_redemptions?: number;
  valid_until?: string;
}

export interface CreateCouponResponseInterface {
  success: boolean;
  coupon: ClientCouponInterface | null;
  message?: string;
}

export interface UpdateCouponInputInterface {
  id: number;
  name?: string;
  is_active?: boolean;
}

export interface UpdateCouponResponseInterface {
  success: boolean;
  coupon: ClientCouponInterface | null;
  message?: string;
}

export interface DeleteCouponInputInterface {
  id: number;
}

export interface DeleteCouponResponseInterface {
  success: boolean;
  message?: string;
}

// Invite Types
export interface ClientInviteInterface {
  id: number;
  name: string;
  description?: string;
  coupon_id?: number;
  is_active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface InviteWithCoupon extends ClientInviteInterface {
  coupon?: {
    id: number;
    name: string;
    percent_off?: number;
    amount_off?: number;
  };
}

export interface InviteAllocationWithUserInterface extends ClientUserInviteAllocationInterface {
  user: ClientUserInterface;
}

export interface InviteDetailInterface extends ClientInviteInterface {
  coupon?: {
    id: number;
    name: string;
    stripe_coupon_id: string;
    percent_off?: number;
    amount_off?: number;
    duration: string;
  };
  allocations: InviteAllocationWithUserInterface[];
}

export interface GetInvitesInputInterface {
  limit?: number;
  search?: string;
  is_active?: boolean;
  orderBy?: string;
  orderDirection?: string;
  beforeId?: number;
  afterId?: number;
  getCount?: boolean;
}

export interface GetInvitesResponseInterface {
  success: boolean;
  invites: InviteWithCoupon[];
  total: number;
  message?: string;
}

export interface GetInviteInputInterface {
  id: number;
}

export interface GetInviteResponseInterface {
  success: boolean;
  invite: InviteDetailInterface | null;
  message?: string;
}

export interface CreateInviteInputInterface {
  name: string;
  description?: string;
  coupon_id?: number;
}

export interface CreateInviteResponseInterface {
  success: boolean;
  invite: ClientInviteInterface | null;
  message?: string;
}

export interface UpdateInviteInputInterface {
  id: number;
  name?: string;
  description?: string;
  coupon_id?: number | null;
  is_active?: boolean;
}

export interface UpdateInviteResponseInterface {
  success: boolean;
  invite: ClientInviteInterface | null;
  message?: string;
}

export interface DeleteInviteInputInterface {
  id: number;
}

export interface DeleteInviteResponseInterface {
  success: boolean;
  message?: string;
}

export interface AssignInviteToUserInputInterface {
  invite_id: number;
  user_id: number;
  quantity: number;
  notes?: string;
}

export interface ClientUserInviteAllocationInterface {
  id: number;
  user_id: number;
  invite_id: number;
  quantity_allocated: number;
  quantity_used: number;
  quantity_remaining: number;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface AssignInviteToUserResponseInterface {
  success: boolean;
  allocation: ClientUserInviteAllocationInterface | null;
  message?: string;
}

export interface UpdateUserInviteAllocationInputInterface {
  id: number;
  quantity_allocated?: number;
  notes?: string;
}

export interface UpdateUserInviteAllocationResponseInterface {
  success: boolean;
  allocation: ClientUserInviteAllocationInterface | null;
  message?: string;
}

export interface DeleteUserInviteAllocationInputInterface {
  inviteId: number;
  allocationId: number;
}

export interface DeleteUserInviteAllocationResponseInterface {
  success: boolean;
  message?: string;
}

export interface GenerateInviteUrlInputInterface {
  inviteId: number;
  allocationId: number;
}

export interface GenerateInviteUrlResponseInterface {
  success: boolean;
  inviteUrl?: string;
  message?: string;
}

// Admin API Types
export interface GetUsersInputInterface {
  limit?: number;
  search?: string;
  userType?: string;
  birthdateFrom?: string; // ISO date string
  birthdateTo?: string; // ISO date string
  hasBeliefOnThought?: number; // Thought ID to filter users who have beliefs on this thought
  // Demographic filters
  education?: string;
  political?: string;
  sex?: string;
  country?: string;
  state?: string;
  city?: string;
  religion?: string;
  // Cursor-based pagination parameters
  orderBy?: string; // 'id' or 'createdAt'
  orderDirection?: string; // 'ASC' or 'DESC'
  beforeId?: number; // For cursor-based pagination
  afterId?: number; // For cursor-based pagination
  beforeCreatedDate?: string; // For cursor-based pagination
  afterCreatedDate?: string; // For cursor-based pagination
  // Count parameter
  getCount?: boolean; // Whether to include total count in response
}

export interface ClientUserInterface {
  account_id: number;
  name: string;
  email: string;
  type: string;
  continent?: string;
  country?: string;
  state?: string;
  city?: string;
  sex?: 'M' | 'F';
  religion?: string;
  political?: string;
  birthdate?: Date;
  education?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date;
  ageCategory: string;
}

export interface GetUsersResponseInterface {
  success: boolean;
  users: ClientUserInterface[];
  total: number;
  message?: string;
}

export interface GetUserInputInterface {
  userId: number;
}

export interface GetUserResponseInterface {
  success: boolean;
  user: ClientUserInterface | null;
  message?: string;
}

export interface GetBeliefsInputInterface {
  limit?: number;
  userId?: number;
  goeUserId?: number; // Greater than or equal to user ID
  loeUserId?: number; // Less than or equal to user ID
  thoughtId?: number; // Filter by specific thought ID
  position?: string; // 'believe', 'disbelieve', 'unsure'
  createdAfter?: string; // ISO date string
  createdBefore?: string; // ISO date string
  supportingBeliefsFor?: number; // Belief ID to find supporting beliefs for
  // Cursor-based pagination parameters
  orderBy?: string; // 'id' or 'createdAt'
  orderDirection?: string; // 'ASC' or 'DESC'
  beforeId?: number; // For cursor-based pagination
  afterId?: number; // For cursor-based pagination
  beforeCreatedDate?: string; // For cursor-based pagination
  afterCreatedDate?: string; // For cursor-based pagination
  // Count parameter
  getCount?: boolean; // Whether to include total count in response
}

export interface ClientBelief {
  id: number;
  content_id?: number;
  user_id: number;
  thought_id: number;
  position: string; // 'believe', 'disbelieve', 'unsure'
  createdAt: Date;
  updatedAt: Date;
  thought?: {
    content_id: number;
    text: string;
    createdAt: Date;
  };
}

export interface GetBeliefsResponseInterface {
  success: boolean;
  beliefs: ClientBelief[];
  total: number;
  message?: string;
}

export interface GetSupportingBeliefsInputInterface {
  limit?: number;
  userId?: number;
  goeUserId?: number; // Greater than or equal to user ID
  loeUserId?: number; // Less than or equal to user ID
  beliefId?: number;
  supportingBeliefId?: number;
  cacheThoughtId?: number;
  cachePosition?: 'believe' | 'disbelieve' | 'unsure';
  cacheSupportingThoughtId?: number;
  cacheSupportingPosition?: 'believe' | 'disbelieve' | 'unsure';
  createdAfter?: string; // ISO date string
  createdBefore?: string; // ISO date string
  updatedAfter?: string; // ISO date string
  updatedBefore?: string; // ISO date string
  // Cursor-based pagination parameters
  orderBy?: string; // 'createdAt' or 'updatedAt'
  orderDirection?: string; // 'ASC' or 'DESC'
  beforeCreatedDate?: string; // For cursor-based pagination
  afterCreatedDate?: string; // For cursor-based pagination
  beforeUpdatedDate?: string; // For cursor-based pagination
  afterUpdatedDate?: string; // For cursor-based pagination
  // Count parameter
  getCount?: boolean; // Whether to include total count in response
  // Include related data
  includeBelief?: boolean; // Include the main belief data
  includeSupportingBelief?: boolean; // Include the supporting belief data
  includeThoughts?: boolean; // Include thought data
}

export interface ClientBeliefSupport {
  user_id: number;
  belief_id: number;
  supporting_belief_id: number;
  cache_thoughtid: number;
  cache_position: 'believe' | 'disbelieve' | 'unsure';
  cache_supportingthoughtid: number;
  cache_supportingposition: 'believe' | 'disbelieve' | 'unsure';
  createdAt: Date;
  updatedAt: Date;
}

export interface ClientBeliefSupportWithRelations extends ClientBeliefSupport {
  belief?: {
    id: number;
    thought_id: number;
    position: 'believe' | 'disbelieve' | 'unsure';
    createdAt: Date;
    thought?: {
      content_id: number;
      text: string;
      createdAt: Date;
    };
  };
  supporting_belief?: {
    id: number;
    thought_id: number;
    position: 'believe' | 'disbelieve' | 'unsure';
    createdAt: Date;
    thought?: {
      content_id: number;
      text: string;
      createdAt: Date;
    };
  };
}

export interface GetSupportingBeliefsResponseInterface {
  success: boolean;
  supporting_beliefs: ClientBeliefSupportWithRelations[];
  total: number;
  message?: string;
}

export interface GetThoughtsInputInterface {
  limit?: number;
  search?: string; // Search in thought text
  createdAfter?: string; // Filter by Thought.createdAt >= date
  createdBefore?: string; // Filter by Thought.createdAt <= date
  isInFeed?: number; // Filter by thoughts that are in a specific feed
  // Cursor-based pagination parameters
  orderBy?: string; // 'id' or 'createdAt'
  orderDirection?: string; // 'ASC' or 'DESC'
  beforeId?: number; // For cursor-based pagination
  afterId?: number; // For cursor-based pagination
  beforeCreatedDate?: string; // For cursor-based pagination
  afterCreatedDate?: string; // For cursor-based pagination
  // Count parameter
  getCount?: boolean; // Whether to include total count in response
}

export interface ClientThoughtWithAuthor {
  content_id: number;
  text: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface GetThoughtsResponseInterface {
  success: boolean;
  thoughts: ClientThoughtWithAuthor[];
  total: number;
  message?: string;
}

export interface ClientFeed {
  content_id: number;
  title: string;
  description: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface GetFeedsInputInterface {
  limit?: number;
  search?: string; // Search in feed title and description
  createdAfter?: string; // Filter by Feed.createdAt >= date
  createdBefore?: string; // Filter by Feed.createdAt <= date
  // Cursor-based pagination parameters
  orderBy?: string; // 'id' or 'createdAt'
  orderDirection?: string; // 'ASC' or 'DESC'
  beforeId?: number; // For cursor-based pagination
  afterId?: number; // For cursor-based pagination
  beforeCreatedDate?: string; // For cursor-based pagination
  afterCreatedDate?: string; // For cursor-based pagination
  // Count parameter
  getCount?: boolean; // Whether to include total count in response
}

export interface GetFeedsResponseInterface {
  success: boolean;
  feeds: ClientFeed[];
  total: number;
  message?: string;
}

export interface GetFeedInputInterface {
  feedId: number;
}

export interface GetFeedResponseInterface {
  success: boolean;
  feed: ClientFeed | null;
  message?: string;
}

export interface GetFeedContentInputInterface {
  feedId: number;
  limit?: number;
  before?: number;
  after?: number;
  orderBy?: string; // 'rank' or 'createdAt'
  orderDirection?: string; // 'ASC' or 'DESC'
  getCount?: boolean;
}

export interface FeedContentItem {
  id: number;
  feed_rank: number;
  content: any; // Using any for now since UniversalClientContent is complex
  createdAt: Date;
  updatedAt: Date;
}

export interface GetFeedContentResponseInterface {
  success: boolean;
  feedContent: FeedContentItem[];
  total: number;
  message?: string;
}

export interface SimulateGetFeedContentInputInterface {
  feedId: number;
  limit?: number;
  before?: number;
  after?: number;
}

export interface SimulateContentData {
  // Common ID property for all content types
  id?: number;
  // For beliefs
  position?: string;
  thought_id?: number;
  // For thoughts
  text?: string;
  // For feeds
  title?: string;
  description?: string;
  // Common properties
  createdAt?: Date;
  updatedAt?: Date;
}

export interface SimulateFeedContent {
  feed_content_id: number;
  feed_rank: number;
  content: {
    id: number;
    type: string;
    author_id: number;
    owner_id: number;
    public: boolean;
    createdAt: Date;
    updatedAt: Date;
    content: SimulateContentData | null;
    author?: {
      account_id: number;
      name: string;
      email: string;
      type: string;
      continent?: string;
      country?: string;
      state?: string;
      city?: string;
      sex?: 'M' | 'F';
      religion?: string;
      political?: string;
      birthdate?: Date;
      education?: string;
      createdAt: Date;
      updatedAt: Date;
      deletedAt?: Date;
      ageCategory: string;
    };
  };
}

export interface SimulateGetFeedContentResponseInterface {
  success: boolean;
  feedContent: SimulateFeedContent[];
  message?: string;
}

export interface GetBeliefInputInterface {
  beliefId: number;
}

export interface GetBeliefResponseInterface {
  success: boolean;
  belief: ClientBelief | null;
  message?: string;
}

export interface GetThoughtInputInterface {
  thoughtId: number;
}

export interface GetThoughtResponseInterface {
  success: boolean;
  thought: ClientThoughtWithAuthor | null;
  message?: string;
}

export interface GenerateRobustBeliefSystemInputInterface {
  beliefId: number;
}

export interface GenerateRobustBeliefSystemResponseInterface {
  success: boolean;
  robust_belief_system?: {
    belief_system: string;
    reasoning: 'CRAZY' | 'VERY_INTELLIGENT' | 'NORMAL';
  };
  message?: string;
}

export interface BeliefInput {
  thought_id?: number;
  belief_id?: number;
  text: string;
  position: 'BELIEVE' | 'DISBELIEVE' | 'UNSURE';
}

export interface GetSupportingBeliefsFromBeliefSystemInputInterface {
  beliefId: number;
  beliefSystem: {
    belief_system: string;
    reasoning: 'CRAZY' | 'VERY_INTELLIGENT' | 'NORMAL';
  };
}

export interface GetSupportingBeliefsFromBeliefSystemResponseInterface {
  success: boolean;
  result?: {
    existing: BeliefInput[];
    new: BeliefInput[];
  };
  message?: string;
}

export interface SaveNewSupportingThoughtAndBeliefInputInterface {
  userId: number;
  beliefId: number;
  newThoughtText: string;
  supportingBeliefPosition: 'BELIEVE' | 'DISBELIEVE' | 'UNSURE';
}

export interface SaveNewSupportingThoughtAndBeliefResponseInterface {
  success: boolean;
  newThought?: {
    content_id: number;
    text: string;
  };
  newBelief?: {
    id: number;
    position: string;
    thought_id: number;
  };
  message?: string;
}

export interface GetSupportingBeliefCountFromBeliefsInputInterface {
  beliefIds: number[];
}

export interface BeliefSupportCount {
  beliefId: number;
  supportingBeliefCount: number;
}

export interface GetSupportingBeliefCountFromBeliefsResponseInterface {
  success: boolean;
  supportingBeliefCounts: BeliefSupportCount[];
  message?: string;
}

export interface LoginAsUserInputInterface {
  userId: number;
}

export interface LoginAsUserResponseInterface {
  success: boolean;
  session?: string;
  message?: string;
}

export interface AddContentToFeedInputInterface {
  feedId: number;
  contentType: 'belief' | 'thought';
  contentId: number; // belief ID or thought content_id
}

export interface AddContentToFeedResponseInterface {
  success: boolean;
  message?: string;
  feedContentId?: number;
  feedRank?: number;
}

export interface UpdateContentOnFeedInputInterface {
  feedContentId: number;
  contentType: 'belief' | 'thought';
  contentId: number; // belief ID or thought content_id
}

export interface UpdateContentOnFeedResponseInterface {
  success: boolean;
  message?: string;
  feedContentId?: number;
  newContentId?: number;
}

export interface GetRelatedThoughtsFromFeedIDInputInterface {
  feedId: number;
}

export interface GetRelatedThoughtsFromFeedIDResponseInterface {
  success: boolean;
  thoughts: ClientThoughtWithAuthor[];
  message?: string;
}

export interface AddSupportingBeliefInputInterface {
  beliefId: number;
  supportingBeliefId: number;
  userId: number;
}

export interface AddSupportingBeliefResponseInterface {
  success: boolean;
  message?: string;
}

export interface CreateAdminBeliefInputInterface {
  userId: number;
  thoughtId: number;
  position: 'believe' | 'disbelieve' | 'unsure';
}

export interface CreateAdminBeliefResponseInterface {
  success: boolean;
  belief?: {
    id: number;
    position: string;
    thought_id: number;
    user_id: number;
  };
  message?: string;
}

export interface GenerateBeliefAndRobustBeliefSystemFromThoughtInputInterface {
  thoughtId: number;
  userId: number;
}

export interface GenerateBeliefAndRobustBeliefSystemFromThoughtResponseInterface {
  success: boolean;
  belief?: {
    id: number;
    position: string;
    thought_id: number;
  };
  robust_belief_system?: {
    belief_system: string;
    reasoning: 'CRAZY' | 'VERY_INTELLIGENT' | 'NORMAL';
  };
  message?: string;
}

export interface GetProbabilitySetFromThoughtIdsInputInterface {
  thoughtIds: number[];
}

export interface BeliefProbabilityMatrix {
  thoughtId: number;
  thought: string;
  propabilitySets: any; // Using any for now since the probability sets structure is complex
  correlationStrengthByCategory: any;
  interestStrengthByCategory: any;
}

export interface GetProbabilitySetFromThoughtIdsResponseInterface {
  success: boolean;
  beliefProbabilityMatrices: BeliefProbabilityMatrix[];
  fromCache: number; // Number of results from database
  fromOpenAI: number; // Number of results from OpenAI
  message?: string;
}

export interface CalculateProbabilisticSupportingBeliefsForUserBatchInputInterface {
  firstUserId: number;
  lastUserId: number;
  thoughtId: number;
}

export interface CalculateProbabilisticSupportingBeliefsForUserBatchResponseInterface {
  success: boolean;
  message: string;
}

export interface GetBeliefAnalyticsInputInterface {
  thoughtId: number;
  category?: 'Sex' | 'Location' | 'Age';
  startDate?: string;
  endDate?: string;
  withBelief?: {
    thoughtId: number;
    position: 'believe' | 'unsure' | 'disbelieve';
  };
}

export interface CategoryData {
  categoryName: string;
  Believe: number;
  Unsure: number;
  Disbelieve: number;
}

export interface GetBeliefAnalyticsResponseInterface {
  success: boolean;
  categoryData: CategoryData[];
  message?: string;
}

export interface AdminLoginInputInterface {
  email: string;
  password: string;
}

export interface AdminLoginResponseInterface {
  success: boolean;
  user?: ClientUserInterface;
  token?: string;
  message?: string;
}

// Define interceptor type with proper Axios typing
export type RequestInterceptor = (request: AxiosRequestConfig) => AxiosRequestConfig;

export class AdminClientApi {
  private apiUrl: string = '';
  private tokenGetter: () => string;
  private interceptors: RequestInterceptor[] = [];
  private cache: AdminClientCache;
  private onUnauthorized?: () => void;

  constructor(apiUrl: string, tokenGetter: () => string, cache?: AdminClientCache) {
    this.apiUrl = apiUrl;
    this.tokenGetter = tokenGetter;
    this.cache = cache || defaultCache;
  }

  public setUnauthorizedHandler(handler: () => void): void {
    this.onUnauthorized = handler;
  }

  public setInterceptor(interceptor: RequestInterceptor): void {
    this.interceptors.push(interceptor);
  }

  public clearInterceptors(): void {
    this.interceptors = [];
  }

  // Cache management methods
  public clearCache(): void {
    this.cache.clear();
  }

  public invalidateCache(pattern?: string): number {
    if (pattern) {
      return this.cache.invalidatePattern(pattern);
    } else {
      this.cache.clear();
      return 0;
    }
  }

  public getCacheStats() {
    return this.cache.getStats();
  }

  // Determine if an API call should be cached
  private shouldCache(endpoint: string, method: string = 'post'): boolean {
    // Only cache GET requests and specific read operations
    if (method.toLowerCase() === 'get') return true;

    // Cache specific read endpoints even if they use POST
    const readEndpoints = [
      '/api/v1/admin/getUsers',
      '/api/v1/admin/getUser',
      '/api/v1/admin/getBeliefs',
      '/api/v1/admin/getSupportingBeliefs',
      '/api/v1/admin/getThoughts',
      '/api/v1/admin/getBelief',
      '/api/v1/admin/getThought',
      // Feed-related APIs (newly added)
      '/api/v1/admin/getFeeds',
      '/api/v1/admin/getFeed',
      '/api/v1/admin/getFeedContent',
      '/api/v1/admin/simulateGetFeedContent',
      // Supporting belief count API (newly added)
      '/api/v1/admin/getSupportingBeliefCountFromBeliefs',
      // AI belief system analysis APIs (newly added)
      '/api/v1/admin/generateRobustBeliefSystem',
      '/api/v1/admin/generateBeliefAndRobustBeliefSystemFromThought',
      '/api/v1/admin/getSupportingBeliefsFromBeliefSystem',
      // Feed-related APIs (newly added)
      '/api/v1/admin/getRelatedThoughtsFromFeedID',
      // Probability set APIs (newly added)
      '/api/v1/admin/getProbabilitySetFromThoughtIds',
    ];

    // Never cache authentication endpoints
    if (endpoint.includes('/login')) return false;

    return readEndpoints.some((readEndpoint) => endpoint.includes(readEndpoint));
  }

  // Get cache TTL for specific endpoints
  private getCacheTTL(endpoint: string): number {
    // User-related endpoints
    if (endpoint.includes('getUser')) return CacheTTL.MEDIUM;
    if (endpoint.includes('getUsers')) return CacheTTL.SHORT;

    // Belief-related endpoints
    if (endpoint.includes('getBelief')) return CacheTTL.MEDIUM;
    if (endpoint.includes('getBeliefs')) return CacheTTL.SHORT;
    if (endpoint.includes('getSupportingBeliefs')) return CacheTTL.SHORT;

    // Thought-related endpoints (optimized from SHORT to MEDIUM)
    if (endpoint.includes('getThought')) return CacheTTL.LONG;
    if (endpoint.includes('getThoughts')) return CacheTTL.MEDIUM; // Changed from SHORT to MEDIUM

    // Feed-related endpoints (newly added)
    if (endpoint.includes('getFeeds')) return CacheTTL.MEDIUM; // Feeds don't change frequently
    if (endpoint.includes('getFeed')) return CacheTTL.MEDIUM; // Single feed metadata is stable
    if (endpoint.includes('getFeedContent')) return CacheTTL.SHORT; // Content can be added/modified
    if (endpoint.includes('simulateGetFeedContent')) return CacheTTL.SHORT; // Dynamic content

    // Count-related endpoints (newly added)
    if (endpoint.includes('getSupportingBeliefCountFromBeliefs')) return CacheTTL.SHORT; // Counts change with new beliefs

    // AI analysis endpoints (newly added)
    if (endpoint.includes('generateRobustBeliefSystem')) return CacheTTL.LONG; // AI analysis is expensive and stable
    if (endpoint.includes('generateBeliefAndRobustBeliefSystemFromThought')) return CacheTTL.LONG; // AI analysis is expensive and stable
    if (endpoint.includes('getSupportingBeliefsFromBeliefSystem')) return CacheTTL.MEDIUM; // Analysis results are fairly stable

    return CacheTTL.MEDIUM; // Default
  }

  // Enhanced axios request method with caching
  public async axiosRequest(
    subpath: string,
    requestBody?: {},
    requestHeaders?: {},
    requestMethod?: 'post' | 'get' | 'put' | 'delete',
    shouldCache: boolean = true
  ) {
    const method = requestMethod || 'post';
    const fullUrl = `${this.apiUrl}${subpath}`;

    // Check cache for read operations
    if (shouldCache && this.shouldCache(subpath, method)) {
      const cacheKey = this.cache.generateKey(subpath, requestBody);
      const cachedData = this.cache.get(cacheKey);

      if (cachedData) {
        return cachedData;
      }
    }

    const token = this.tokenGetter();
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...requestHeaders,
    };

    let config: AxiosRequestConfig = {
      url: fullUrl,
      method,
      headers,
    };

    if (requestBody && (method === 'post' || method === 'put')) {
      config.data = requestBody;
    } else if (requestBody && method === 'get') {
      config.params = requestBody;
    }

    // Apply interceptors
    for (const interceptor of this.interceptors) {
      config = interceptor(config);
    }

    try {
      const response = await axios(config);
      const responseData = response.data;

      // Cache successful read operations
      if (this.shouldCache(subpath, method) && responseData.success) {
        const cacheKey = this.cache.generateKey(subpath, requestBody);
        const ttl = this.getCacheTTL(subpath);
        this.cache.set(cacheKey, responseData, ttl);
      }

      // Invalidate related cache entries for write operations
      if (!this.shouldCache(subpath, method)) {
        this.invalidateRelatedCache(subpath);
      }

      return responseData;
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Clear auth data and trigger logout
        localStorage.removeItem('authToken');
        localStorage.removeItem('adminUser');
        
        // Call unauthorized handler if set (for navigation to login)
        if (this.onUnauthorized) {
          this.onUnauthorized();
        }

        const message = error.response?.status === 401 
          ? 'Authentication failed. Please log in again.' 
          : 'Access denied. Administrator privileges required.';
        
        throw new AdminApiError(
          error.response?.status === 401 ? 'UNAUTHORIZED' : 'FORBIDDEN',
          message,
          error.response.data,
          error.response.status
        );
      }
      if (error.response?.status >= 500) {
        throw new AdminApiError(
          'SERVER_ERROR',
          'Internal server error. Please try again later.',
          error.response.data,
          error.response.status
        );
      }
      if (error.response?.data?.message) {
        throw new AdminApiError(
          'API_ERROR',
          error.response.data.message,
          error.response.data,
          error.response.status
        );
      }
      throw new AdminApiError('NETWORK_ERROR', 'Network error occurred', error);
    }
  }

  // Invalidate cache entries related to write operations
  private invalidateRelatedCache(endpoint: string): void {
    // AI Supporting Beliefs - invalidate beliefs and thoughts
    if (endpoint.includes('saveNewSupportingThoughtAndBelief')) {
      this.cache.invalidateEndpoint('/api/v1/admin/getBeliefs');
      this.cache.invalidateEndpoint('/api/v1/admin/getSupportingBeliefs');
      this.cache.invalidateEndpoint('/api/v1/admin/getThoughts');
      // Also invalidate supporting belief counts since new beliefs are added
      this.cache.invalidateEndpoint('/api/v1/admin/getSupportingBeliefCountFromBeliefs');
    }

    // Feed Content Management - invalidate feed-related caches
    if (endpoint.includes('addContentToFeed')) {
      // Invalidate feed content caches (both admin and simulation)
      this.cache.invalidateEndpoint('/api/v1/admin/getFeedContent');
      this.cache.invalidateEndpoint('/api/v1/admin/simulateGetFeedContent');
      // Also invalidate supporting belief counts if beliefs were added to feed
      this.cache.invalidateEndpoint('/api/v1/admin/getSupportingBeliefCountFromBeliefs');
    }

    // User Authentication - invalidate user-related caches
    if (endpoint.includes('loginAsUser')) {
      // Clear user-specific cached data when switching user context
      this.cache.invalidatePattern('getUser');
      this.cache.invalidatePattern('getBeliefs');
      this.cache.invalidatePattern('getThoughts');
    }

    // AI Belief System Generation - invalidate analysis caches
    if (endpoint.includes('generateRobustBeliefSystem')) {
      // Clear related AI analysis results
      this.cache.invalidateEndpoint('/api/v1/admin/getSupportingBeliefsFromBeliefSystem');
    }

    // Pattern-based invalidation for entity relationships
    // Any write operation that affects beliefs should invalidate counts and supporting beliefs
    if (endpoint.includes('belief') || endpoint.includes('Belief')) {
      this.cache.invalidateEndpoint('/api/v1/admin/getSupportingBeliefCountFromBeliefs');
      this.cache.invalidateEndpoint('/api/v1/admin/getSupportingBeliefs');
    }

    // Any write operation that affects thoughts should invalidate belief-related caches
    if (endpoint.includes('thought') || endpoint.includes('Thought')) {
      this.cache.invalidatePattern('getBelief'); // Beliefs depend on thoughts
    }

    // Any write operation that affects feeds should invalidate feed content
    if (endpoint.includes('feed') || endpoint.includes('Feed')) {
      this.cache.invalidateEndpoint('/api/v1/admin/getFeedContent');
      this.cache.invalidateEndpoint('/api/v1/admin/simulateGetFeedContent');
    }
  }

  public setApiUrl(url: string) {
    this.apiUrl = url;
  }

  public getApiUrl(): string {
    return this.apiUrl;
  }

  // Admin API Methods
  public async getUsers(input?: GetUsersInputInterface): Promise<GetUsersResponseInterface> {
    return this.axiosRequest('/api/v1/admin/users', input, {}, 'get');
  }

  public async getUser(input: GetUserInputInterface): Promise<GetUserResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getUser', input, {}, 'post');
  }

  public async getBeliefs(input?: GetBeliefsInputInterface): Promise<GetBeliefsResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getBeliefs', input, {}, 'post');
  }

  public async getSupportingBeliefs(
    input?: GetSupportingBeliefsInputInterface
  ): Promise<GetSupportingBeliefsResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getSupportingBeliefs', input, {}, 'post');
  }

  public async getThoughts(
    input?: GetThoughtsInputInterface
  ): Promise<GetThoughtsResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getThoughts', input);
  }

  public async getFeeds(input?: GetFeedsInputInterface): Promise<GetFeedsResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getFeeds', input);
  }

  public async getFeed(input: GetFeedInputInterface): Promise<GetFeedResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getFeed', input);
  }

  public async getFeedContent(
    input: GetFeedContentInputInterface
  ): Promise<GetFeedContentResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getFeedContent', input);
  }

  public async simulateGetFeedContent(
    input: SimulateGetFeedContentInputInterface
  ): Promise<SimulateGetFeedContentResponseInterface> {
    return this.axiosRequest('/api/v1/admin/simulateGetFeedContent', input);
  }

  public async getBelief(input: GetBeliefInputInterface): Promise<GetBeliefResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getBelief', input, {}, 'post');
  }

  public async getThought(input: GetThoughtInputInterface): Promise<GetThoughtResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getThought', input, {}, 'post');
  }

  public async generateRobustBeliefSystem(
    input: GenerateRobustBeliefSystemInputInterface
  ): Promise<GenerateRobustBeliefSystemResponseInterface> {
    return this.axiosRequest('/api/v1/admin/generateRobustBeliefSystem', input, {}, 'post');
  }

  public async getSupportingBeliefsFromBeliefSystem(
    input: GetSupportingBeliefsFromBeliefSystemInputInterface
  ): Promise<GetSupportingBeliefsFromBeliefSystemResponseInterface> {
    return this.axiosRequest(
      '/api/v1/admin/getSupportingBeliefsFromBeliefSystem',
      input,
      {},
      'post'
    );
  }

  public async saveNewSupportingThoughtAndBelief(
    input: SaveNewSupportingThoughtAndBeliefInputInterface
  ): Promise<SaveNewSupportingThoughtAndBeliefResponseInterface> {
    return this.axiosRequest('/api/v1/admin/saveNewSupportingThoughtAndBelief', input, {}, 'post');
  }

  public async getSupportingBeliefCountFromBeliefs(
    input: GetSupportingBeliefCountFromBeliefsInputInterface
  ): Promise<GetSupportingBeliefCountFromBeliefsResponseInterface> {
    return this.axiosRequest(
      '/api/v1/admin/getSupportingBeliefCountFromBeliefs',
      input,
      {},
      'post'
    );
  }

  public async loginAsUser(
    input: LoginAsUserInputInterface
  ): Promise<LoginAsUserResponseInterface> {
    return this.axiosRequest(`/api/v1/admin/loginAsUser/${input.userId}`, {}, {}, 'get');
  }

  public async addContentToFeed(
    input: AddContentToFeedInputInterface
  ): Promise<AddContentToFeedResponseInterface> {
    return this.axiosRequest('/api/v1/admin/addContentToFeed', input, {}, 'post');
  }

  public async updateContentOnFeed(
    input: UpdateContentOnFeedInputInterface
  ): Promise<UpdateContentOnFeedResponseInterface> {
    return this.axiosRequest('/api/v1/admin/updateContentOnFeed', input, {}, 'post');
  }

  public async getRelatedThoughtsFromFeedID(
    input: GetRelatedThoughtsFromFeedIDInputInterface
  ): Promise<GetRelatedThoughtsFromFeedIDResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getRelatedThoughtsFromFeedID', input, {}, 'post');
  }

  public async addSupportingBelief(
    input: AddSupportingBeliefInputInterface
  ): Promise<AddSupportingBeliefResponseInterface> {
    return this.axiosRequest('/api/v1/admin/addSupportingBelief', input, {}, 'post');
  }

  public async createAdminBelief(
    input: CreateAdminBeliefInputInterface
  ): Promise<CreateAdminBeliefResponseInterface> {
    return this.axiosRequest('/api/v1/admin/createBelief', input, {}, 'post');
  }

  public async generateBeliefAndRobustBeliefSystemFromThought(
    input: GenerateBeliefAndRobustBeliefSystemFromThoughtInputInterface
  ): Promise<GenerateBeliefAndRobustBeliefSystemFromThoughtResponseInterface> {
    return this.axiosRequest(
      '/api/v1/admin/generateBeliefAndRobustBeliefSystemFromThought',
      input,
      {},
      'post'
    );
  }

  public async getProbabilitySetFromThoughtIds(
    input: GetProbabilitySetFromThoughtIdsInputInterface
  ): Promise<GetProbabilitySetFromThoughtIdsResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getProbabilitySetFromThoughtIds', input, {}, 'post');
  }

  public async calculateProbabilisticSupportingBeliefsForUserBatch(
    input: CalculateProbabilisticSupportingBeliefsForUserBatchInputInterface
  ): Promise<CalculateProbabilisticSupportingBeliefsForUserBatchResponseInterface> {
    return this.axiosRequest(
      '/api/v1/admin/calculateProbabilisticSupportingBeliefsForUserBatch',
      input,
      {},
      'post'
    );
  }

  public async getBeliefAnalytics(
    input: GetBeliefAnalyticsInputInterface
  ): Promise<GetBeliefAnalyticsResponseInterface> {
    return this.axiosRequest('/api/v1/admin/getBeliefAnalytics', input, {}, 'post');
  }

  public async adminLogin(
    input: AdminLoginInputInterface
  ): Promise<AdminLoginResponseInterface> {
    return this.axiosRequest('/api/v1/admin/login', input, {}, 'post');
  }

  // Coupon Management Methods
  public async getCoupons(input?: GetCouponsInputInterface, shouldCache = false): Promise<GetCouponsResponseInterface> {
    const params = new URLSearchParams();
    if (input) {
      if (input.limit) params.append('limit', input.limit.toString());
      if (input.search) params.append('search', input.search);
      if (input.is_active !== undefined) params.append('is_active', input.is_active.toString());
      if (input.duration) params.append('duration', input.duration);
      if (input.orderBy) params.append('orderBy', input.orderBy);
      if (input.orderDirection) params.append('orderDirection', input.orderDirection);
      if (input.beforeId) params.append('beforeId', input.beforeId.toString());
      if (input.afterId) params.append('afterId', input.afterId.toString());
      if (input.getCount !== undefined) params.append('getCount', input.getCount.toString());
    }
    const queryString = params.toString();
    const url = `/api/v1/admin/coupons${queryString ? `?${queryString}` : ''}`;
    return this.axiosRequest(url, {}, {}, 'get', shouldCache);
  }

  public async getCoupon(input: GetCouponInputInterface): Promise<GetCouponResponseInterface> {
    return this.axiosRequest(`/api/v1/admin/coupons/${input.id}`, {}, {}, 'get');
  }

  public async createCoupon(
    input: CreateCouponInputInterface
  ): Promise<CreateCouponResponseInterface> {
    return this.axiosRequest('/api/v1/admin/coupons/create', input, {}, 'post');
  }

  public async updateCoupon(
    input: UpdateCouponInputInterface
  ): Promise<UpdateCouponResponseInterface> {
    const { id, ...updateData } = input;
    return this.axiosRequest(`/api/v1/admin/coupons/${id}/update`, updateData, {}, 'post');
  }

  public async deleteCoupon(
    input: DeleteCouponInputInterface
  ): Promise<DeleteCouponResponseInterface> {
    return this.axiosRequest(`/api/v1/admin/coupons/${input.id}/delete`, {}, {}, 'delete');
  }

  // Invite Management Methods
  public async getInvites(input?: GetInvitesInputInterface, shouldCache = false): Promise<GetInvitesResponseInterface> {
    const params = new URLSearchParams();
    if (input) {
      if (input.limit) params.append('limit', input.limit.toString());
      if (input.search) params.append('search', input.search);
      if (input.is_active !== undefined) params.append('is_active', input.is_active.toString());
      if (input.orderBy) params.append('orderBy', input.orderBy);
      if (input.orderDirection) params.append('orderDirection', input.orderDirection);
      if (input.beforeId) params.append('beforeId', input.beforeId.toString());
      if (input.afterId) params.append('afterId', input.afterId.toString());
      if (input.getCount !== undefined) params.append('getCount', input.getCount.toString());
    }
    const queryString = params.toString();
    const url = `/api/v1/admin/invites${queryString ? `?${queryString}` : ''}`;
    return this.axiosRequest(url, {}, {}, 'get', shouldCache);
  }

  public async getInvite(input: GetInviteInputInterface, shouldCache = false): Promise<GetInviteResponseInterface> {
    return this.axiosRequest(`/api/v1/admin/invites/${input.id}`, {}, {}, 'get', shouldCache);
  }

  public async createInvite(
    input: CreateInviteInputInterface
  ): Promise<CreateInviteResponseInterface> {
    return this.axiosRequest('/api/v1/admin/invites/create', input, {}, 'post');
  }

  public async updateInvite(
    input: UpdateInviteInputInterface
  ): Promise<UpdateInviteResponseInterface> {
    const { id, ...updateData } = input;
    return this.axiosRequest(`/api/v1/admin/invites/${id}/update`, updateData, {}, 'post');
  }

  public async deleteInvite(
    input: DeleteInviteInputInterface
  ): Promise<DeleteInviteResponseInterface> {
    return this.axiosRequest(`/api/v1/admin/invites/${input.id}/delete`, {}, {}, 'delete');
  }

  public async assignInviteToUser(
    input: AssignInviteToUserInputInterface
  ): Promise<AssignInviteToUserResponseInterface> {
    return this.axiosRequest(`/api/v1/admin/invites/${input.invite_id}/assign`, input, {}, 'post');
  }

  public async updateUserInviteAllocation(
    input: UpdateUserInviteAllocationInputInterface
  ): Promise<UpdateUserInviteAllocationResponseInterface> {
    return this.axiosRequest(`/api/v1/admin/invites/allocations/${input.id}/update`, input, {}, 'post');
  }

  public async deleteUserInviteAllocation(
    input: DeleteUserInviteAllocationInputInterface
  ): Promise<DeleteUserInviteAllocationResponseInterface> {
    return this.axiosRequest(`/api/v1/admin/invites/allocations/${input.allocationId}/delete`, {}, {}, 'delete');
  }

  public async generateInviteUrl(
    input: GenerateInviteUrlInputInterface
  ): Promise<GenerateInviteUrlResponseInterface> {
    return this.axiosRequest(`/api/v1/admin/invites/allocations/${input.allocationId}/generate-url`, {}, {}, 'get');
  }
}

export class AdminApiError extends Error {
  code: string;
  details?: any;
  statusCode?: number;

  constructor(code: string, message: string, details?: any, statusCode?: number) {
    super(message);
    this.name = 'AdminApiError';
    this.code = code;
    this.details = details;
    this.statusCode = statusCode;
  }
}

// Export cache system for advanced users
export { AdminClientCache, defaultCache, CacheTTL } from './cache';
export type { CacheEntry, CacheConfig } from './cache';
